import { useEffect, useRef, useState } from "react";
import { Viewer } from "@photo-sphere-viewer/core";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import { EquirectangularTilesAdapter } from "@photo-sphere-viewer/equirectangular-tiles-adapter";

import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";

import {
  exteriorScenes as scenes,
  exteriorTourOrder as tourOrder,
} from "../data/scenes";

export default function ExteriorViewer({
  sceneKey,
  setSceneKey,
}) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const fadeRef = useRef(null);

  const autoTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);

  const markersRef = useRef(null);

  // ✅ continuous rotation refs
  const animationFrameRef = useRef(null);
  const rotationRef = useRef(true);

  const [isOpen, setIsOpen] = useState(false);

  const exteriorScenes = Object.entries(scenes);

  const capitalize = (str) =>
    str
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join("_");

  // =====================================================
  // CONTINUOUS 360 ROTATION
  // =====================================================

  const runCameraTour = () => {
    rotationRef.current = true;

    const rotate = () => {
      if (!viewerRef.current || !rotationRef.current)
        return;

      const position =
        viewerRef.current.getPosition();

      viewerRef.current.rotate({
        yaw: position.yaw + 0.001,
        pitch: position.pitch,
      });

      animationFrameRef.current =
        requestAnimationFrame(rotate);
    };

    rotate();
  };

  // =====================================================
  // VIEWER INIT
  // =====================================================

  useEffect(() => {
    if (!containerRef.current) return;

    // destroy previous viewer
    if (viewerRef.current) {
      viewerRef.current.destroy();
      viewerRef.current = null;
    }

    const timeout = setTimeout(() => {
      viewerRef.current = new Viewer({
        container: containerRef.current,

        adapter: EquirectangularTilesAdapter,

        panorama: {
          width: 16384,
          cols: 32,
          rows: 16,

          baseUrl: `/images/tiles/${capitalize(
            sceneKey
          )}/preview.jpg`,

          levels: [
            { width: 512, cols: 1, rows: 1 },
            { width: 1024, cols: 2, rows: 1 },
            { width: 2048, cols: 4, rows: 2 },
            { width: 4096, cols: 8, rows: 4 },
            { width: 8192, cols: 16, rows: 8 },
            { width: 16384, cols: 32, rows: 16 },
          ],

          tileUrl: (col, row, level) => {
            return `/images/tiles/${capitalize(
              sceneKey
            )}/${level}/${col}_${row}.jpg`;
          },
        },

        navbar: ["zoom", "fullscreen"],

        defaultYaw:
          scenes[sceneKey].defaultYaw || "0deg",

        defaultPitch:
          scenes[sceneKey].defaultPitch ||
          "0deg",

        plugins: [[MarkersPlugin, {}]],
      });

      const markers =
        viewerRef.current.getPlugin(
          MarkersPlugin
        );

      markersRef.current = markers;

      loadHotspots(markers, sceneKey);

      // =================== automatic expand line and collapse line  ==================================
      // viewerRef.current.addEventListener(
      //   "position-updated",
      //   () => {

      //     const markers = markersRef.current;

      //     if (!markers || !viewerRef.current)
      //       return;

      //     const viewer = viewerRef.current;

      //     scenes[sceneKey].hotspots.forEach((h) => {

      //       if (h.type !== "point-label" || h.fixed)
      //         return;

      //       const marker =
      //         markers.getMarker(h.id);

      //       console.log("Marker:", marker?.domElement);
      //       if (!marker?.domElement)
      //         return;

      //       const wrapper =
      //         marker.domElement.querySelector(
      //           ".road-marker"
      //         );

      //       if (!wrapper)
      //         return;

      //       // convert marker position to screen coordinates

      //       const toRad = (value) => {
      //         if (typeof value === "string") {
      //           return parseFloat(value) * Math.PI / 180;
      //         }
      //         return value;
      //       };

      //       const point =
      //         viewer.dataHelper.vector3ToViewerCoords(
      //           viewer.dataHelper.sphericalCoordsToVector3({
      //             yaw: toRad(h.yaw),
      //             pitch: toRad(h.pitch)
      //           })
      //         );

      //       const centerX =
      //         viewer.container.clientWidth / 2;

      //       const centerY =
      //         viewer.container.clientHeight / 2;

      //       const dx =
      //         point.x - centerX;

      //       const dy =
      //         point.y - centerY;

      //       const distance =
      //         Math.sqrt(
      //           dx * dx + dy * dy
      //         );

      //       // marker close to center of screen
      //       if (distance < 250) {

      //         wrapper.classList.add(
      //           "expanded"
      //         );

      //       } else {

      //         wrapper.classList.remove(
      //           "expanded"
      //         );
      //       }

      //     });
      //   }
      // );

      // =====================================================
      // READY
      // =====================================================

      viewerRef.current.addEventListener(
        "ready",
        () => {
          console.log(
            "✅ Scene loaded:",
            sceneKey
          );

          runCameraTour();
        }
      );

      // =====================================================
      // HOTSPOT CLICK
      // =====================================================

      markers.addEventListener(
        "select-marker",
        (e) => {
          const hotspot =
            scenes[sceneKey].hotspots.find(
              (h) => h.id === e.marker.id
            );

          if (hotspot?.target) {
            triggerSceneChange(
              hotspot.target,
              hotspot
            );
          }
        }
      );

      // =====================================================
      // USER INTERACTION
      // =====================================================

      viewerRef.current.addEventListener(
        "user-interaction",
        () => {
          if (!viewerRef.current) return;

          console.log("⏸ Paused");

          // stop rotation
          rotationRef.current = false;

          if (animationFrameRef.current) {
            cancelAnimationFrame(
              animationFrameRef.current
            );
          }

          // clear timers
          if (autoTimerRef.current) {
            clearTimeout(autoTimerRef.current);
          }

          if (resumeTimerRef.current) {
            clearTimeout(resumeTimerRef.current);
          }

          // resume after idle
          resumeTimerRef.current = setTimeout(
            () => {
              if (!viewerRef.current) return;

              console.log("▶️ Resume");

              runCameraTour();

              autoTimerRef.current =
                setTimeout(() => {
                  const hotspots =
                    scenes[sceneKey].hotspots;

                  const nextHotspot =
                    hotspots.find(
                      (h) => h.target
                    );

                  const currentIndex =
                    tourOrder.indexOf(sceneKey);

                  const nextScene =
                    tourOrder[
                    (currentIndex + 1) %
                    tourOrder.length
                    ];

                  triggerSceneChange(
                    nextScene,
                    nextHotspot
                  );
                }, 5000);
            },
            5000
          );
        }
      );
    }, 50);

    // =====================================================
    // CLEANUP
    // =====================================================

    return () => {
      clearTimeout(timeout);

      rotationRef.current = false;

      if (animationFrameRef.current) {
        cancelAnimationFrame(
          animationFrameRef.current
        );
      }

      if (autoTimerRef.current) {
        clearTimeout(autoTimerRef.current);
      }

      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
      }

      viewerRef.current?.destroy();
      viewerRef.current = null;
    };
  }, [sceneKey]);

  // =====================================================
  // SCENE TRANSITION
  // =====================================================

  const triggerSceneChange = async (
    nextScene,
    hotspot
  ) => {
    const viewer = viewerRef.current;
    const fadeEl = fadeRef.current;

    if (!viewer || !fadeEl) return;

    try {
      // stop rotation during transition
      rotationRef.current = false;

      if (animationFrameRef.current) {
        cancelAnimationFrame(
          animationFrameRef.current
        );
      }

      fadeEl.style.opacity = 1;

      await new Promise((res) =>
        setTimeout(res, 400)
      );

      setSceneKey(nextScene);

      const waitForNewScene = () =>
        new Promise((resolve) => {
          const check = () => {
            if (viewerRef.current) {
              viewerRef.current.addEventListener(
                "ready",
                resolve,
                {
                  once: true,
                }
              );
            } else {
              setTimeout(check, 50);
            }
          };

          check();
        });

      await waitForNewScene();

      const newViewer = viewerRef.current;

      if (!newViewer) return;

      newViewer.zoom(0);

      if (fadeRef.current) {
        fadeRef.current.style.opacity = 0;
      }

      runCameraTour();
    } catch (e) {
      console.log("Transition interrupted");
    }
  };

  // =====================================================
  // HOTSPOTS
  // =====================================================

  const loadHotspots = (markers, sceneKey) => {
    markers.clearMarkers();

    scenes[sceneKey].hotspots.forEach((h) => {


      // Custom point label
      if (h.type === "point-label") {
        markers.addMarker({
          id: h.id,

          position: {
            yaw: h.yaw,
            pitch: h.pitch,
          },
          anchor: "center bottom",
          // html: `<div class="road-marker  ${h.fixed ? "expanded fixed-marker" : ""}"
          html: `<div class="road-marker expanded fixed-marker"
          style="
        --line-height:${h.lineHeight || 90}px;
      "
      >

            <div class="road-line"></div>

            <div class="road-dot">
                <div class="road-pulse"></div>
            </div>

          <div
  class="road-label ${h.direction === "right" ? "right" : ""}"
>
  ${h.label}
</div>

        </div>`,

        });

        return;
      }
      else if (h.type === "polygon") {

        markers.addMarker({
          id: h.id,

          polygon: h.polygon,

          svgStyle: h.svgStyle || {
            fill: "rgba(255, 193, 7, 0.35)",
            stroke: "#ffc107",
            strokeWidth: "3px",
          },
        });
      }
      else {
        let icon = "/icons/arrow.svg";

        markers.addMarker({
          id: h.id,

          position: {
            yaw: h.yaw,
            pitch: h.pitch,
          },

          html: `
          <div style="
          display:flex;
      flex-direction:column;
      align-items:center;
      justify-content:center;
      text-align:center;
        ">
              <img
            src=${icon} // or .svg
            style={{
              width: "10px",
              height: "10px",
              cursor: "pointer",
              filter: "drop-shadow(0 0 6px rgba(0,0,0,0.8))",
            }}
          />
           
          </div>
        `,

          anchor: "center center",
        });
      }
    });
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100dvh",
        overflow: "hidden",
      }}
    >
      {/* VIEWER */}

      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          background: "#000",
        }}
      />

      {/* FADE */}

      <div
        ref={fadeRef}
        style={{
          position: "absolute",
          inset: 0,
          background: "#000",
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 0.5s ease",
        }}
      />

      {/* SIDE PANEL */}

      <div
        style={{
          position: "absolute",
          bottom: window.innerWidth < 768 ? 20 : 40,
          right: window.innerWidth < 768 ? 10 : 20,
          zIndex: 10,
        }}
      >
        {/* COLLAPSED */}

        {!isOpen && (
          <div
            onClick={() => setIsOpen(true)}
            style={{
              position: "relative",
              width:
                window.innerWidth < 768
                  ? 90
                  : 120,

              height:
                window.innerWidth < 768
                  ? 60
                  : 80,

              cursor: "pointer",
            }}
          >
            {exteriorScenes
              .slice(0, 4)
              .map(([key, s], i) => (
                <img
                  key={key}
                  src={
                    s.thumbnail || s.panorama
                  }
                  alt=""
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 10,
                    top: i * -5,
                    left: i * 5,
                    opacity: 1 - i * 0.2,
                    zIndex: 10 - i,
                    boxShadow:
                      "0 4px 10px rgba(0,0,0,0.5)",
                  }}
                />
              ))}
          </div>
        )}

        {/* EXPANDED */}

        {isOpen && (
          <div
            className="hide-scrollbar"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,

              background:
                "rgba(0,0,0,0.6)",

              padding:
                window.innerWidth < 768
                  ? 8
                  : 10,

              borderRadius: 12,

              backdropFilter: "blur(6px)",

              maxHeight: "75vh",

              overflowY: "auto",

              width:
                window.innerWidth < 768
                  ? 100
                  : 130,
            }}
          >
            {/* CLOSE */}

            <div
              onClick={() => setIsOpen(false)}
              style={{
                alignSelf: "flex-end",
                cursor: "pointer",
                color: "#fff",
                fontSize: 18,
              }}
            >
              ✕
            </div>

            {exteriorScenes.map(
              ([key, s]) => (
                <div
                  key={key}
                  onClick={() => {
                    if (key === sceneKey)
                      return;

                    const hotspot =
                      scenes[
                        sceneKey
                      ]?.hotspots?.find(
                        (h) =>
                          h.target === key
                      );

                    if (hotspot) {
                      triggerSceneChange(
                        key,
                        hotspot
                      );
                    } else {
                      setSceneKey(key);
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    textAlign: "center",
                  }}
                >
                  <img
                    src={
                      s.thumbnail ||
                      s.panorama
                    }
                    alt={s.name || key}
                    style={{
                      width: "100%",

                      height:
                        window.innerWidth <
                          768
                          ? 55
                          : 70,

                      objectFit: "cover",

                      borderRadius: 6,

                      border:
                        key === sceneKey
                          ? "2px solid #fff"
                          : "2px solid transparent",

                      opacity:
                        key === sceneKey
                          ? 1
                          : 0.7,
                    }}
                  />

                  <div
                    style={{
                      marginTop: 4,
                      color: "#fff",

                      fontSize:
                        window.innerWidth <
                          768
                          ? 10
                          : 11,

                      wordBreak:
                        "break-word",
                    }}
                  >
                    {s.name || key}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}